import * as React from 'react';
import { reportError } from '@/utils/reportError';

interface UseVoiceRecorderReturn {
  isRecording: boolean;
  recordingTime: number;
  audioData: number[];
  spokenWordCount: number;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<{ blob: Blob; duration: number } | null>;
  cancelRecording: () => void;
  error: string | null;
}

interface SpeechRecognitionResult {
  readonly length: number;
  [index: number]: { transcript: string };
}

interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEventPayload extends Event {
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventPayload) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: Event) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionInstance;

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

/**
 * Manages browser microphone recording via MediaRecorder + WebAudio visualization.
 * Uses an invocation counter to safely handle React StrictMode double-mounts.
 * Runs SpeechRecognition in parallel to count spoken words for karaoke highlighting.
 */
export const useVoiceRecorder = (): UseVoiceRecorderReturn => {
  const [isRecording, setIsRecording] = React.useState(false);
  const [recordingTime, setRecordingTime] = React.useState(0);
  const [audioData, setAudioData] = React.useState<number[]>([]);
  const [spokenWordCount, setSpokenWordCount] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);

  const mediaRecorder = React.useRef<MediaRecorder | null>(null);
  const audioChunks = React.useRef<Blob[]>([]);
  const audioContext = React.useRef<AudioContext | null>(null);
  const analyser = React.useRef<AnalyserNode | null>(null);
  const dataArray = React.useRef<Uint8Array<ArrayBuffer> | null>(null);
  const source = React.useRef<MediaStreamAudioSourceNode | null>(null);
  const timerInterval = React.useRef<number | null>(null);
  const animationFrame = React.useRef<number | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const recognitionRef = React.useRef<SpeechRecognitionInstance | null>(null);
  const invocationId = React.useRef(0);

  const MAX_BARS = 60;

  const cleanup = React.useCallback(() => {
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = null;
    }
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.abort();
      } catch {}
      recognitionRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (audioContext.current && audioContext.current.state !== 'closed') {
      audioContext.current.close().catch(() => {
        /* already closed */
      });
    }

    mediaRecorder.current = null;
    analyser.current = null;
    source.current = null;
    dataArray.current = null;
    setIsRecording(false);
  }, []);

  const analyzeAudio = React.useCallback(() => {
    if (!analyser.current || !dataArray.current) return;

    const buffer = dataArray.current as Uint8Array<ArrayBuffer>;
    analyser.current.getByteTimeDomainData(buffer);

    let sum = 0;
    const len = buffer.length;
    for (let i = 0; i < len; i++) {
      const val = buffer[i] ?? 128;
      const sample = (val - 128) / 128;
      sum += sample * sample;
    }
    const rms = Math.sqrt(sum / len);
    const volume = Math.min(100, Math.max(5, rms * 1000));

    setAudioData((prev) => {
      const newData = [...prev, volume];
      return newData.length > MAX_BARS ? newData.slice(newData.length - MAX_BARS) : newData;
    });

    animationFrame.current = requestAnimationFrame(analyzeAudio);
  }, []);

  /** Starts SpeechRecognition to count words spoken for karaoke-style highlighting. */
  const startSpeechRecognition = React.useCallback(() => {
    const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) return;

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event: SpeechRecognitionEventPayload) => {
      let fullTranscript = '';
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result?.[0]) fullTranscript += result[0].transcript;
      }
      const count = fullTranscript.trim().split(/\s+/).filter(Boolean).length;
      setSpokenWordCount(count);
    };

    recognition.onend = () => {
      if (recognitionRef.current === recognition) {
        try {
          recognition.start();
        } catch {}
      }
    };

    recognition.onerror = () => {
      /* speech recognition errors are non-critical */
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
    } catch {}
  }, []);

  const startRecording = React.useCallback(async () => {
    const myId = ++invocationId.current;

    cleanup();
    setError(null);
    setAudioData([]);
    setRecordingTime(0);
    setSpokenWordCount(0);
    audioChunks.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      if (myId !== invocationId.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      mediaRecorder.current = new MediaRecorder(stream, { mimeType });

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorder.current.start(100);

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      audioContext.current = new AudioCtx();
      if (audioContext.current.state === 'suspended') {
        await audioContext.current.resume();
      }
      analyser.current = audioContext.current.createAnalyser();
      analyser.current.fftSize = 512;
      analyser.current.smoothingTimeConstant = 0.5;
      source.current = audioContext.current.createMediaStreamSource(stream);
      source.current.connect(analyser.current);

      dataArray.current = new Uint8Array(new ArrayBuffer(analyser.current.frequencyBinCount));

      setIsRecording(true);
      timerInterval.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      analyzeAudio();
      startSpeechRecognition();
    } catch (err) {
      reportError('useVoiceRecorder.startRecording', err);
      setError('Microphone access denied or not available.');
      cleanup();
    }
  }, [cleanup, analyzeAudio, startSpeechRecognition]);

  const stopRecording = React.useCallback(
    async (): Promise<{
      blob: Blob;
      duration: number;
    } | null> =>
      new Promise((resolve) => {
        if (!mediaRecorder.current || mediaRecorder.current.state === 'inactive') {
          cleanup();
          resolve(null);
          return;
        }

        mediaRecorder.current.onstop = () => {
          const type = mediaRecorder.current ? mediaRecorder.current.mimeType : 'audio/webm';
          const blob = new Blob(audioChunks.current, { type });
          const duration = recordingTime;
          cleanup();
          resolve({ blob, duration });
        };

        mediaRecorder.current.stop();
      }),
    [cleanup, recordingTime]
  );

  const cancelRecording = React.useCallback(() => {
    cleanup();
    setAudioData([]);
    setRecordingTime(0);
    setSpokenWordCount(0);
  }, [cleanup]);

  React.useEffect(() => () => cleanup(), [cleanup]);

  return {
    isRecording,
    recordingTime,
    audioData,
    spokenWordCount,
    startRecording,
    stopRecording,
    cancelRecording,
    error,
  };
};
